import { useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, ApiError } from "../../lib/api-client";

interface RedeemedBook {
  id: string;
  title: string;
  slug: string;
  coverR2Key: string | null;
  formats: string[];
}

interface RedeemResponse {
  ok: boolean;
  message: string;
  voucher: {
    id: string;
    code: string;
    title: string;
  };
  books: RedeemedBook[];
  downloadUrls: {
    pdf: string | null;
    epub: string | null;
  };
}

export function RedeemPage() {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RedeemResponse | null>(null);

  async function handleRedeem(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await apiClient.post<RedeemResponse>("/consumer/redeem", {
        code: code.trim(),
        email: email.trim(),
        name: name.trim() || undefined,
      });

      setResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não foi possível resgatar o código informado.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setResult(null);
    setCode("");
    setError(null);
  }

  return (
    <div style={{ maxWidth: 680, margin: "var(--space-6) auto", padding: "0 var(--space-3)" }}>
      <header style={{ textAlign: "center", marginBottom: "var(--space-6)" }}>
        <div
          style={{
            display: "inline-block",
            padding: "3px 10px",
            borderRadius: "var(--radius-sm)",
            background: "rgba(184, 115, 51, 0.12)",
            color: "var(--color-brass)",
            fontSize: "var(--text-xs)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "var(--tracking-label)",
            marginBottom: "var(--space-2)",
          }}
        >
          Acesso e Download por Código
        </div>
        <h1 style={{ fontSize: "var(--text-3xl)", marginBottom: "var(--space-2)" }}>Resgatar Livro ou Voucher</h1>
        <p className="text-muted" style={{ maxWidth: 520, margin: "0 auto", fontSize: "var(--text-base)" }}>
          Insira o código promocional, voucher de evento ou código fornecido pela sua instituição/empresa para liberar o exemplar digital na sua conta.
        </p>
      </header>

      {result ? (
        <div className="card" style={{ padding: "var(--space-6)", borderTop: "4px solid var(--color-success, #22c55e)" }}>
          <div style={{ textAlign: "center", marginBottom: "var(--space-5)" }}>
            <span style={{ fontSize: "40px" }}>🎉</span>
            <h2 style={{ fontSize: "var(--text-2xl)", color: "var(--color-success, #15803d)", marginTop: "var(--space-2)" }}>
              Código Resgatado com Sucesso!
            </h2>
            <p className="text-muted" style={{ fontSize: "var(--text-sm)" }}>
              {result.message}
            </p>
          </div>

          <div style={{ background: "var(--color-surface-sunken)", padding: "var(--space-4)", borderRadius: "var(--radius-md)", marginBottom: "var(--space-5)" }}>
            <h3 style={{ fontSize: "var(--text-base)", margin: "0 0 var(--space-3)", color: "var(--color-brass)" }}>
              Obras Liberadas para Download Imediato:
            </h3>

            {result.books.map((book) => (
              <div
                key={book.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "var(--space-3)",
                  padding: "var(--space-3) 0",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                <div>
                  <strong style={{ display: "block", fontSize: "var(--text-lg)" }}>{book.title}</strong>
                  <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                    Formatos suportados: {book.formats.map((f) => f.toUpperCase()).join(" / ")}
                  </span>
                </div>

                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  {result.downloadUrls.pdf && (
                    <a
                      href={result.downloadUrls.pdf}
                      className="btn"
                      style={{ fontSize: "var(--text-xs)", padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      📥 Baixar PDF
                    </a>
                  )}
                  {result.downloadUrls.epub && (
                    <a
                      href={result.downloadUrls.epub}
                      className="btn btn-secondary"
                      style={{ fontSize: "var(--text-xs)", padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      📖 Baixar EPUB
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "var(--space-3)" }}>
            <Link to="/minha-biblioteca" className="btn" style={{ flex: 1, textAlign: "center" }}>
              Ir para Minha Biblioteca
            </Link>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              style={{ flex: 1 }}
            >
              Resgatar Outro Código
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleRedeem} className="card" style={{ padding: "var(--space-6)" }}>
          {error && (
            <div
              role="alert"
              className="notice-error"
              style={{
                marginBottom: "var(--space-4)",
                padding: "var(--space-3)",
                background: "rgba(220, 38, 38, 0.1)",
                color: "#dc2626",
                borderRadius: "var(--radius-sm)",
                fontSize: "var(--text-sm)",
              }}
            >
              {error}
            </div>
          )}

          <div className="field">
            <label htmlFor="voucher-code" style={{ fontWeight: 600 }}>
              Código do Voucher ou Token de Download *
            </label>
            <input
              id="voucher-code"
              type="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Ex.: DATASCI-2026 ou CORP-LIVRO-2026"
              style={{ textTransform: "uppercase", fontSize: "var(--text-lg)", letterSpacing: "1px" }}
              autoFocus
            />
            <span className="text-muted" style={{ fontSize: "11px" }}>
              Dica de teste: utilize o código <code>DATASCI-2026</code> ou <code>CORP-LIVRO-2026</code>.
            </span>
          </div>

          <div className="field" style={{ marginTop: "var(--space-3)" }}>
            <label htmlFor="user-email" style={{ fontWeight: 600 }}>
              Seu E-mail (Pessoal ou Corporativo) *
            </label>
            <input
              id="user-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu.email@empresa.com.br"
            />
            <span className="text-muted" style={{ fontSize: "11px" }}>
              O livro será permanentemente vinculado a este e-mail na sua biblioteca digital.
            </span>
          </div>

          <div className="field" style={{ marginTop: "var(--space-3)" }}>
            <label htmlFor="user-name">
              Seu Nome Completo (opcional)
            </label>
            <input
              id="user-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do leitor"
            />
          </div>

          <button
            type="submit"
            className="btn"
            disabled={loading}
            style={{ width: "100%", marginTop: "var(--space-5)", padding: "12px", fontSize: "var(--text-base)" }}
          >
            {loading ? "Validando código e liberando obra..." : "Resgatar Livro e Baixar"}
          </button>

          <div
            style={{
              marginTop: "var(--space-5)",
              paddingTop: "var(--space-4)",
              borderTop: "1px solid var(--color-border)",
              fontSize: "var(--text-xs)",
              color: "var(--color-text-muted)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--space-2)",
            }}
          >
            <span>Já tem uma conta com compras anteriores?</span>
            <Link to="/minha-biblioteca" style={{ fontWeight: 600 }}>
              Acessar Minha Biblioteca →
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
